import { type FC, useState } from 'react';
import { AutoAwesome, CheckCircleOutline, QuizOutlined } from '@mui/icons-material';
import { Box, Button, Card, CardContent, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import generateTechnicalAssessment from '@services/api/technical-assessment/generate/generate';
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './EmptyAssessment.translations.json';

type SupportedLanguage = keyof typeof translations;

interface EmptyAssessmentProps {
  offerId: number;
  onRefetch?: () => void;
}

const DARK_CARD_BG = '#121824';
const DARK_BORDER = '#1e2638';

const EmptyAssessment: FC<EmptyAssessmentProps> = ({ offerId, onRefetch }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;

  const handleGenerate = async () => {
    setIsGenerating(true);

    try {
      const response = await generateTechnicalAssessment(offerId);

      enqueueSnackbar(
        response?.message || (response?.success ? content.success : content.error),
        { variant: response?.success ? 'success' : 'error' }
      );
      onRefetch?.();
    } catch (error) {
      enqueueSnackbar(content.retry, { variant: 'error' });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box sx={{ width: '100%', minHeight: 380, display: 'flex', justifyContent: 'center', px: { xs: 0, sm: 2 } }}>
      <Card sx={{ maxWidth: 760, width: '100%', mx: 'auto', backgroundColor: DARK_CARD_BG, border: `1px solid ${DARK_BORDER}`, borderRadius: 2, boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.3)' }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 }, '&:last-child': { pb: { xs: 3, sm: 5 } } }}>
          <Box sx={{ width: 52, height: 52, display: 'grid', placeItems: 'center', mb: 3, borderRadius: 1.5, backgroundColor: 'rgba(76, 175, 80, 0.14)', color: '#7bd389' }}>
            <QuizOutlined fontSize="large" />
          </Box>

          <Typography variant="h5" fontWeight={700} color="#ffffff" mb={1}>{content.title}</Typography>
          <Typography color="#aab4c5" lineHeight={1.6} sx={{ maxWidth: 540 }}>
            {content.description}
          </Typography>

          <List disablePadding sx={{ mt: 3, mb: 4 }}>
            {[content.benefitOne, content.benefitTwo].map((benefit) => (
              <ListItem key={benefit} disableGutters sx={{ py: 0.45 }}>
                <ListItemIcon sx={{ minWidth: 28, color: '#7bd389' }}><CheckCircleOutline fontSize="small" /></ListItemIcon>
                <ListItemText primary={benefit} primaryTypographyProps={{ color: '#d9e0eb', fontSize: '0.9rem', fontWeight: 500 }} />
              </ListItem>
            ))}
          </List>

          <Button variant="contained" onClick={handleGenerate} disabled={isGenerating} startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesome />} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, px: 3, py: 1.2 }}>
            {isGenerating ? content.generating : content.generate}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmptyAssessment;